import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Share2, Star, Users, Target, Award, Bell, Gift } from 'lucide-react';

export const ReferralsTab = ({ referralStats }) => {
  const getMedalDisplay = (idx) => {
    if (idx === 0) return <span className="text-lg">&#x1F947;</span>;
    if (idx === 1) return <span className="text-lg">&#x1F948;</span>;
    if (idx === 2) return <span className="text-lg">&#x1F949;</span>;
    return <span className="text-gray-400 text-sm">{idx + 1}</span>;
  };

  return (
    <div className="space-y-6">
      {/* Referral KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border shadow-sm" data-testid="referral-stat-total">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Share2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{referralStats?.total_referrals || 0}</p>
              <p className="text-xs text-gray-500">Referidos Exitosos</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border shadow-sm" data-testid="referral-stat-bonus">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center">
              <Star className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">+{referralStats?.total_bonus_days_awarded || 0}</p>
              <p className="text-xs text-gray-500">Días Bonus Otorgados</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border shadow-sm" data-testid="referral-stat-referred">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{referralStats?.total_referred_users || 0}</p>
              <p className="text-xs text-gray-500">Usuarios Referidos</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border shadow-sm" data-testid="referral-stat-conversion">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-purple-100 flex items-center justify-center">
              <Target className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{referralStats?.referral_conversion_rate || 0}%</p>
              <p className="text-xs text-gray-500">Conversión Referidos</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Referrers */}
      <Card className="border shadow-lg" data-testid="top-referrers-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Award className="w-5 h-5 text-amber-500" />Top Embajadores
          </CardTitle>
          <CardDescription>Usuarios que más clientes han referido</CardDescription>
        </CardHeader>
        <CardContent>
          {referralStats?.top_referrers?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">#</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Código</TableHead>
                  <TableHead className="text-center">Referidos</TableHead>
                  <TableHead className="text-center">Días Bonus</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {referralStats.top_referrers.map((r, idx) => (
                  <TableRow key={r.id}>
                    <TableCell>{getMedalDisplay(idx)}</TableCell>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell className="text-gray-500 text-sm">{r.email}</TableCell>
                    <TableCell>
                      <code className="bg-gray-100 px-2 py-0.5 rounded text-xs font-mono">{r.referral_code}</code>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full text-xs font-bold">{r.referral_count}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-amber-600 font-semibold">+{r.bonus_days}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-10 text-gray-400">
              <Share2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>Aún no hay referidos registrados</p>
              <p className="text-sm mt-1">Los clientes pueden compartir su código desde su panel</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Referral Activity */}
      {referralStats?.recent_activity?.length > 0 && (
        <Card className="border shadow-lg" data-testid="referral-activity-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Bell className="w-5 h-5 text-blue-500" />Actividad Reciente de Referidos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {referralStats.recent_activity.map((a) => (
                <div key={`${a.referrer_name}-${a.referred_name}-${a.created_at}`} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                    <Gift className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm"><strong>{a.referrer_name}</strong> refirió a <strong>{a.referred_name}</strong></p>
                    <p className="text-xs text-gray-400">{a.created_at ? new Date(a.created_at).toLocaleString('es-MX') : ''}</p>
                  </div>
                  <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs font-bold shrink-0">+{a.bonus_days} días</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
